import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  FormControlLabel,
  FormGroup,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CheckCircleOutline as CheckIcon,
  ClearOutlined as ClearIcon,
  CloudDoneOutlined as WebIcon,
  DevicesOutlined as DevicesIcon,
  EventOutlined as EventIcon,
  InfoOutlined as InfoIcon,
  NotificationsActiveOutlined as NotifyIcon,
  PersonOutlineOutlined as PersonIcon,
  RefreshOutlined as RefreshIcon,
  SelectAllOutlined as SelectAllIcon,
  SendOutlined as SendIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import moment from "moment";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { toast } from "react-toastify";
import api from "../../services/api";
import ModernLoader from "../../components/common/ModernLoader";
import { registerPush } from "../../utils/push";

const DEFAULT_NOTIFICATION = {
  title: "",
  body: "",
  clickAction: "/my-bookings",
};

const DEFAULT_CHANNELS = {
  webPush: true,
  firebase: true,
};

const AdminNotifications = () => {
  const theme = useTheme();
  const { user: clerkUser, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [registeringBrowser, setRegisteringBrowser] = useState(false);
  const [testingBrowser, setTestingBrowser] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [sendToAll, setSendToAll] = useState(false);
  const [deliveryModes, setDeliveryModes] = useState(DEFAULT_CHANNELS);
  const [notification, setNotification] = useState(DEFAULT_NOTIFICATION);

  const fetchRecipients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/notifications/users-with-tokens");
      setRecipients(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to load notification recipients",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecipients();
  }, [fetchRecipients]);

  const reachableRecipients = useMemo(
    () => recipients.filter((user) => user.hasWebPush || user.hasFcmToken),
    [recipients],
  );

  const currentRecipient = useMemo(() => {
    const clerkId = clerkUser?.id;
    const email = clerkUser?.primaryEmailAddress?.emailAddress?.toLowerCase();

    return (
      recipients.find((user) => {
        const userEmail = (user.email || "").toLowerCase();
        return (
          (clerkId && user.clerkId === clerkId) ||
          (email && userEmail === email)
        );
      }) || null
    );
  }, [clerkUser?.id, clerkUser?.primaryEmailAddress?.emailAddress, recipients]);

  const currentBrowserRegistered = !!(
    currentRecipient &&
    (currentRecipient.hasWebPush || currentRecipient.hasFcmToken)
  );

  const selectedRecipients = useMemo(() => {
    const selectedIds = new Set(Object.keys(rowSelection));
    return recipients.filter((user) => selectedIds.has(user.id));
  }, [recipients, rowSelection]);

  const activeRecipients = sendToAll ? reachableRecipients : selectedRecipients;

  const selectedChannels = useMemo(() => {
    return Object.entries(deliveryModes)
      .filter(([, enabled]) => enabled)
      .map(([channel]) => (channel === "webPush" ? "web-push" : "firebase"));
  }, [deliveryModes]);

  const stats = useMemo(() => {
    return {
      total: recipients.length,
      reachable: reachableRecipients.length,
      unregistered: Math.max(recipients.length - reachableRecipients.length, 0),
      selected: selectedRecipients.length,
      webPush: recipients.filter((user) => user.hasWebPush).length,
      firebase: recipients.filter((user) => user.hasFcmToken).length,
    };
  }, [recipients, reachableRecipients.length, selectedRecipients.length]);

  const selectAllRecipients = useCallback(() => {
    const nextSelection = {};
    recipients.forEach((user) => {
      nextSelection[user.id] = true;
    });
    setRowSelection(nextSelection);
  }, [recipients]);

  const clearSelection = useCallback(() => {
    setRowSelection({});
  }, []);

  const toggleDeliveryMode = useCallback((mode) => {
    setDeliveryModes((previous) => ({
      ...previous,
      [mode]: !previous[mode],
    }));
  }, []);

  const handleRegisterCurrentBrowser = useCallback(async () => {
    if (!isSignedIn || !clerkUser) {
      toast.warning("Sign in first to register this browser");
      return;
    }

    setRegisteringBrowser(true);
    try {
      const token = await getToken();
      if (!token) {
        toast.error("Could not get an auth token for this session");
        return;
      }

      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      const result = await registerPush(token, { forceRefresh: true });

      if (result.success) {
        toast.success("This browser is now registered for push testing");
        if (currentRecipient?.id) {
          setRowSelection({ [currentRecipient.id]: true });
        }
        await fetchRecipients();
        return;
      }

      if (result.reason === "PERMISSION_DENIED") {
        toast.warning("Notification permission was denied in the browser");
        return;
      }

      toast.error(
        result.error || result.reason || "Browser registration failed",
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Browser registration failed",
      );
    } finally {
      setRegisteringBrowser(false);
    }
  }, [clerkUser, currentRecipient?.id, fetchRecipients, getToken, isSignedIn]);

  const handleTestCurrentBrowser = useCallback(async () => {
    if (!isSignedIn || !clerkUser) {
      toast.warning("Sign in first to test this browser");
      return;
    }

    setTestingBrowser(true);
    try {
      const token = await getToken();
      if (!token) {
        toast.error("Could not get an auth token for this session");
        return;
      }

      api.defaults.headers.common.Authorization = `Bearer ${token}`;

      const registerResult = await registerPush(token, { forceRefresh: true });
      if (!registerResult.success || !registerResult.subscription) {
        toast.error(
          registerResult.error ||
            registerResult.reason ||
            "Could not prepare browser subscription",
        );
        return;
      }

      const testPayload = {
        title: notification.title.trim() || "BookMyCinema test notification",
        body:
          notification.body.trim() ||
          "This is a push test from the admin console.",
        icon: "/logo192.png",
        url: notification.clickAction.trim() || "/my-bookings",
      };

      const response = await api.post("/push/test-public", {
        subscription: registerResult.subscription,
        payload: testPayload,
      });

      if (response.data?.success) {
        toast.success("Test notification sent to this browser");
      } else {
        const backendError =
          response.data?.result?.error ||
          response.data?.error ||
          response.data?.result?.reason;
        toast.error(
          backendError
            ? `Test send failed: ${backendError}`
            : "The browser was prepared, but the test send did not confirm success",
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Browser test failed",
      );
    } finally {
      setTestingBrowser(false);
    }
  }, [
    clerkUser,
    getToken,
    isSignedIn,
    notification.body,
    notification.clickAction,
    notification.title,
  ]);

  const handleSend = async () => {
    const title = notification.title.trim();
    const body = notification.body.trim();
    const clickAction = notification.clickAction.trim() || "/";

    if (!title || !body) {
      toast.warning("Please enter a notification title and message body");
      return;
    }

    if (selectedChannels.length === 0) {
      toast.warning("Choose at least one delivery channel");
      return;
    }

    if (!activeRecipients.length) {
      toast.warning(
        sendToAll
          ? "No reachable recipients found"
          : "Select at least one user who has a push channel registered",
      );
      return;
    }

    setSending(true);
    try {
      const response = await api.post("/notifications/send", {
        title,
        body,
        clickAction,
        sendToAll,
        userIds: sendToAll ? [] : selectedRecipients.map((user) => user.id),
        channels: selectedChannels,
      });

      const summary = response.data?.summary || {};
      const webPushSent =
        summary.webPush?.sent ?? summary.webPush?.successCount ?? 0;
      const firebaseSent = summary.firebase?.successCount ?? 0;
      const totalDelivered = webPushSent + firebaseSent;

      if (totalDelivered === 0) {
        toast.warning(
          "The request was processed, but none of the selected users have a registered push channel yet. Register this browser or use the browser test button to verify delivery.",
        );
      } else {
        toast.success(
          `Delivered to ${totalDelivered} device(s). Web push: ${webPushSent}. Firebase tokens: ${firebaseSent}.`,
        );
      }
      setNotification(DEFAULT_NOTIFICATION);
      setSendToAll(false);
      clearSelection();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to send notifications",
      );
    } finally {
      setSending(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Identity",
        size: 260,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={row.original.imageUrl}
              sx={{
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                fontWeight: 900,
              }}
            >
              {row.original.name?.charAt(0)?.toUpperCase() || <PersonIcon />}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 850, lineHeight: 1.15 }}>
                {row.original.name || "Anonymous User"}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
                {row.original.email || "No email"}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        accessorKey: "role",
        header: "Role",
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue() || "user";
          const isAdmin = value === "admin";
          return (
            <Chip
              label={value.toUpperCase()}
              size="small"
              color={isAdmin ? "error" : "default"}
              variant={isAdmin ? "filled" : "outlined"}
              sx={{ fontWeight: 900, letterSpacing: "0.04em" }}
            />
          );
        },
      },
      {
        id: "channels",
        header: "Reachability",
        size: 220,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {row.original.hasWebPush ? (
              <Chip
                icon={<WebIcon sx={{ fontSize: "14px !important" }} />}
                label="Web push"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 800 }}
              />
            ) : null}
            {row.original.hasFcmToken ? (
              <Chip
                icon={<DevicesIcon sx={{ fontSize: "14px !important" }} />}
                label={`Firebase • ${row.original.deviceType || "web"}`}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ fontWeight: 800 }}
              />
            ) : null}
            {!row.original.hasWebPush && !row.original.hasFcmToken ? (
              <Chip
                label="Not registered"
                size="small"
                variant="outlined"
                sx={{ fontWeight: 800, opacity: 0.7 }}
              />
            ) : null}
          </Stack>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Joined",
        size: 150,
        Cell: ({ cell }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <EventIcon
              sx={{ fontSize: 18, color: "text.secondary", opacity: 0.75 }}
            />
            <Typography sx={{ fontWeight: 700, color: "text.secondary" }}>
              {cell.getValue()
                ? moment(cell.getValue()).format("MMM DD, YYYY")
                : "Unknown"}
            </Typography>
          </Stack>
        ),
      },
    ],
    [theme],
  );

  const table = useMaterialReactTable({
    columns,
    data: recipients,
    getRowId: (row) => row.id,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableRowSelection: true,
    enableSelectAll: true,
    enableStickyHeader: true,
    initialState: {
      density: "comfortable",
      showGlobalFilter: true,
    },
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    renderTopToolbarCustomActions: () => (
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.25}
        alignItems={{ xs: "flex-start", md: "center" }}
        sx={{ flexWrap: "wrap" }}
      >
        <Chip
          icon={<CheckIcon sx={{ fontSize: "14px !important" }} />}
          label={`${stats.reachable} reachable`}
          size="small"
          color="success"
          variant="outlined"
          sx={{ fontWeight: 800 }}
        />
        <Chip
          icon={<InfoIcon sx={{ fontSize: "14px !important" }} />}
          label={`${stats.unregistered} not registered`}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 800 }}
        />
        <Chip
          icon={<SelectAllIcon sx={{ fontSize: "14px !important" }} />}
          label={`${stats.selected} selected`}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 800 }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SelectAllIcon />}
            onClick={selectAllRecipients}
          >
            Select all users
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={clearSelection}
          >
            Clear selection
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchRecipients}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>
    ),
    muiSearchTextFieldProps: {
      placeholder: "Search name, email, or role",
      variant: "outlined",
      sx: { minWidth: { xs: "100%", md: 280 } },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: 760,
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "24px",
        border: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        bgcolor: theme.palette.background.paper,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        bgcolor: alpha(theme.palette.background.default, 0.5),
        py: 2.25,
        fontWeight: 900,
        fontSize: "0.7rem",
        color: theme.palette.text.secondary,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        opacity: row.original.hasWebPush || row.original.hasFcmToken ? 1 : 0.5,
      },
    }),
    state: {
      rowSelection,
      globalFilter,
      isLoading: loading,
      showProgressBars: loading,
    },
  });

  if (loading && recipients.length === 0) {
    return <ModernLoader text="Loading notification recipients" />;
  }

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            gap={2}
            mb={3}
          >
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 950,
                  letterSpacing: "-0.04em",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.9rem" },
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "14px",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    display: "flex",
                    flexShrink: 0,
                  }}
                >
                  <NotifyIcon sx={{ fontSize: { xs: 22, md: 28 } }} />
                </Box>
                Notification Command Center
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontWeight: 700, mt: 1 }}
              >
                Send browser push to Windows, macOS, Linux, and Android
                browsers, or use Firebase tokens for app-backed devices.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={`${stats.total} users`}
                variant="outlined"
                sx={{ fontWeight: 800 }}
              />
              <Chip
                label={`${stats.reachable} reachable`}
                color="success"
                variant="outlined"
                sx={{ fontWeight: 800 }}
              />
              <Chip
                label={`${stats.webPush} web push`}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 800 }}
              />
              <Chip
                label={`${stats.firebase} Firebase`}
                color="secondary"
                variant="outlined"
                sx={{ fontWeight: 800 }}
              />
            </Stack>
          </Stack>

          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{
              mb: 3,
              borderRadius: "18px",
              alignItems: "center",
            }}
          >
            Broadcast mode sends to every registered user. Use row checkboxes to
            target any smaller group, then register a browser first to make push
            delivery testable.
          </Alert>

          {reachableRecipients.length === 0 ? (
            <Alert
              severity="warning"
              icon={<DevicesIcon />}
              sx={{
                mb: 3,
                borderRadius: "18px",
                alignItems: "center",
              }}
            >
              {currentBrowserRegistered
                ? "This browser is already registered, but the list currently has no other reachable devices. You can still send to selected users, and the backend will only deliver to users with subscriptions."
                : "No browsers or Firebase tokens are registered yet. Use the button on the right to register this browser, then refresh and send a real test notification to yourself."}
            </Alert>
          ) : null}

          <Grid container spacing={3}>
            <Grid item xs={12} lg={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "24px",
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 850, mb: 0.5 }}>
                      Compose notification
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Build the payload once and choose delivery channels below.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: "18px",
                      border: `1px solid ${theme.palette.divider}`,
                      bgcolor: alpha(theme.palette.background.default, 0.35),
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                      alignItems="center"
                    >
                      <Chip
                        size="small"
                        label={
                          currentBrowserRegistered
                            ? "Browser registered"
                            : "Browser not registered"
                        }
                        color={currentBrowserRegistered ? "success" : "warning"}
                        variant="outlined"
                        sx={{ fontWeight: 800 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleRegisterCurrentBrowser}
                        disabled={
                          registeringBrowser || testingBrowser || !isSignedIn
                        }
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {registeringBrowser
                          ? "Registering..."
                          : "Register browser"}
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleTestCurrentBrowser}
                        disabled={
                          registeringBrowser || testingBrowser || !isSignedIn
                        }
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {testingBrowser ? "Sending test..." : "Test browser"}
                      </Button>
                    </Stack>
                  </Box>

                  <TextField
                    fullWidth
                    label="Title"
                    value={notification.title}
                    onChange={(event) =>
                      setNotification((current) => ({
                        ...current,
                        title: event.target.value,
                      }))
                    }
                  />

                  <TextField
                    fullWidth
                    label="Message"
                    multiline
                    minRows={4}
                    value={notification.body}
                    onChange={(event) =>
                      setNotification((current) => ({
                        ...current,
                        body: event.target.value,
                      }))
                    }
                  />

                  <TextField
                    fullWidth
                    label="Click action"
                    helperText="Use a path like /my-bookings or a full URL"
                    value={notification.clickAction}
                    onChange={(event) =>
                      setNotification((current) => ({
                        ...current,
                        clickAction: event.target.value,
                      }))
                    }
                  />

                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={sendToAll}
                          onChange={(event) =>
                            setSendToAll(event.target.checked)
                          }
                        />
                      }
                      label="Broadcast to all registered users"
                    />
                  </FormGroup>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "18px",
                      border: `1px solid ${theme.palette.divider}`,
                      bgcolor: alpha(theme.palette.background.default, 0.45),
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 850, mb: 1 }}
                    >
                      Delivery channels
                    </Typography>
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={deliveryModes.webPush}
                            onChange={() => toggleDeliveryMode("webPush")}
                          />
                        }
                        label="Web push"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={deliveryModes.firebase}
                            onChange={() => toggleDeliveryMode("firebase")}
                          />
                        }
                        label="Firebase token"
                      />
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 1.25,
                        color: "text.secondary",
                      }}
                    >
                      Web push covers desktop and mobile browsers. Firebase
                      tokens help if you also have app or token-backed clients.
                    </Typography>
                  </Box>

                  <Alert
                    severity="info"
                    icon={<DevicesIcon />}
                    sx={{ borderRadius: "18px" }}
                  >
                    Browser push is supported across Windows, macOS, Android,
                    and other modern browsers with notification permission
                    enabled.
                  </Alert>

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<SendIcon />}
                    onClick={handleSend}
                    disabled={
                      sending ||
                      selectedChannels.length === 0 ||
                      (!sendToAll && activeRecipients.length === 0)
                    }
                    sx={{
                      py: 1.5,
                      borderRadius: "16px",
                      fontWeight: 900,
                      textTransform: "none",
                      boxShadow: `0 12px 24px -10px ${alpha(theme.palette.primary.main, 0.45)}`,
                    }}
                  >
                    {sending
                      ? "Sending..."
                      : sendToAll
                        ? `Broadcast to ${reachableRecipients.length} users`
                        : `Send to ${selectedRecipients.length} selected`}
                  </Button>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={8}>
              <Paper
                elevation={0}
                sx={{
                  p: 0,
                  borderRadius: "24px",
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.background.paper,
                  overflow: "hidden",
                }}
              >
                {loading ? <LinearProgress /> : null}
                <MaterialReactTable table={table} />
              </Paper>
            </Grid>
          </Grid>
        </motion.div>
    </Box>
  );
};

export default AdminNotifications;
